import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Checkmark24Regular,
  Delete24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { useApi } from '../auth/useApi.ts';
import { useI18n } from '../i18n/context.tsx';
import { useMe } from '../auth/useMe.ts';

interface UserRow {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'viewer';
  status: 'pending' | 'active' | 'denied';
  createdAt: string;
  lastLoginAt: string | null;
}

const useStyles = makeStyles({
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' },
  headerText: { display: 'grid', gap: '4px' },
  subtitle: { color: tokens.colorNeutralForeground3 },
  spinnerWrap: { padding: '40px' },
  actions: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 },
  self: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  errorWrap: { marginTop: '16px' },
});

export function UsersPage() {
  const styles = useStyles();
  const qc = useQueryClient();
  const { request } = useApi();
  const { t } = useI18n();
  const me = useMe();

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => request<UserRow[]>('/admin/users'),
  });

  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['users'] });
    qc.invalidateQueries({ queryKey: ['me'] });
  };

  const approve = useMutation({
    mutationFn: (id: string) => request(`/admin/users/${id}/approve`, { method: 'POST' }),
    onSuccess: invalidate,
  });
  const deny = useMutation({
    mutationFn: (id: string) => request(`/admin/users/${id}/deny`, { method: 'POST' }),
    onSuccess: invalidate,
  });
  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: 'admin' | 'viewer' }) =>
      request(`/admin/users/${id}/role`, { method: 'POST', body: JSON.stringify({ role }) }),
    onSuccess: invalidate,
  });
  const del = useMutation({
    mutationFn: (id: string) => request(`/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const statusBadge = (status: UserRow['status']) => {
    switch (status) {
      case 'active':
        return <Badge appearance="filled" color="success">{t('users', 'statusActive')}</Badge>;
      case 'pending':
        return <Badge appearance="filled" color="warning">{t('users', 'statusPending')}</Badge>;
      case 'denied':
        return <Badge appearance="filled" color="danger">{t('users', 'statusDenied')}</Badge>;
      default:
        // Defensive fallback — if the server ever returns an unexpected status,
        // render something readable instead of `undefined` which throws in cells.
        return <Badge appearance="outline">{String(status)}</Badge>;
    }
  };

  const roleBadge = (role: UserRow['role']) =>
    role === 'admin' ? (
      <Badge appearance="outline" color="brand">
        {t('users', 'roleAdmin')}
      </Badge>
    ) : (
      <Badge appearance="outline">{t('users', 'roleViewer')}</Badge>
    );

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <Title2>{t('users', 'title')}</Title2>
          <Text className={styles.subtitle}>{t('users', 'subtitle')}</Text>
        </div>
      </div>

      <Card>
        {users.isLoading ? (
          <div className={styles.spinnerWrap}><Spinner /></div>
        ) : users.isError ? (
          <div className={styles.spinnerWrap}>
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>{t('common', 'saveFailed')}</MessageBarTitle>
                {users.error instanceof Error ? users.error.message : null}
              </MessageBarBody>
            </MessageBar>
          </div>
        ) : !users.data || users.data.length === 0 ? (
          <div className={styles.spinnerWrap}>
            <Text>{t('users', 'emptyState')}</Text>
          </div>
        ) : (
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('common', 'email')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'name')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'status')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'role')}</TableHeaderCell>
                <TableHeaderCell>{t('users', 'lastLogin')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'createdAt')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'actions')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.data?.map((u) => {
                const isSelf = me.data?.id === u.id;
                return (
                  <TableRow key={u.id}>
                    <TableCell>
                      <span className={styles.mono}>{u.email}</span>
                      {isSelf ? <> <span className={styles.self}>(you)</span></> : null}
                    </TableCell>
                    <TableCell>{u.name ?? '—'}</TableCell>
                    <TableCell>{statusBadge(u.status)}</TableCell>
                    <TableCell>{roleBadge(u.role)}</TableCell>
                    <TableCell>
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : t('common', 'never')}
                    </TableCell>
                    <TableCell>{new Date(u.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <div className={styles.actions}>
                        {u.status !== 'active' ? (
                          <Button
                            appearance="primary"
                            size="small"
                            icon={<Checkmark24Regular />}
                            disabled={approve.isPending}
                            onClick={() => approve.mutate(u.id)}
                          >
                            {t('common', 'approve')}
                          </Button>
                        ) : null}
                        {u.status !== 'denied' && !isSelf ? (
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<Dismiss24Regular />}
                            disabled={deny.isPending}
                            onClick={() => deny.mutate(u.id)}
                          >
                            {t('common', 'deny')}
                          </Button>
                        ) : null}
                        {u.status === 'active' ? (
                          <Button
                            appearance="subtle"
                            size="small"
                            disabled={setRole.isPending}
                            onClick={() =>
                              setRole.mutate({
                                id: u.id,
                                role: u.role === 'admin' ? 'viewer' : 'admin',
                              })
                            }
                          >
                            {u.role === 'admin' ? t('users', 'makeViewer') : t('users', 'makeAdmin')}
                          </Button>
                        ) : null}
                        {!isSelf ? (
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<Delete24Regular />}
                            onClick={() => setDeleteTarget(u)}
                          />
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {approve.isError || deny.isError || setRole.isError || del.isError ? (
        <div className={styles.errorWrap}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{t('common', 'saveFailed')}</MessageBarTitle>
              {(approve.error || deny.error || setRole.error || del.error) instanceof Error
                ? ((approve.error || deny.error || setRole.error || del.error) as Error).message
                : null}
            </MessageBarBody>
          </MessageBar>
        </div>
      ) : null}

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(_, d) => { if (!d.open) setDeleteTarget(null); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('common', 'confirmDeleteTitle')}</DialogTitle>
            <DialogContent>
              {deleteTarget
                ? t('users', 'confirmDelete', { email: deleteTarget.email })
                : null}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{t('common', 'cancel')}</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                icon={<Delete24Regular />}
                onClick={() => {
                  if (deleteTarget) del.mutate(deleteTarget.id);
                  setDeleteTarget(null);
                }}
              >
                {t('common', 'delete')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
